import React, { useState, useEffect } from 'react';
import { useIntl } from 'react-intl';
import PropTypes from 'prop-types';
import { Box } from '@mui/material';
import { useSetAtom } from 'jotai';
import PanToolAltRoundedIcon from '@mui/icons-material/PanToolAltRounded';
import LeftBar from './LeftBar';
import RightBar from './RightBar';
import { CONTENT_WRAPPERS } from '../../Utils/constants';
import ContentError from '../../../../Components/Error/ContentError';
import { openSnackbarAdviseAtom } from '../../../../Atoms';
import getErrorMessage from '../../../../Helpers/Errors/getErrorMessage';
import decodeBase64Auto from '../../../../Helpers/decode';

const EditorWrapper = function EditorWrapper({
  editorType = '',
  templatesCallback = () => {},
  createCallback = () => {},
  editCallback = () => {},
}) {
  const intl = useIntl();
  const showSnackbar = useSetAtom(openSnackbarAdviseAtom);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [msgType, setMsgType] = useState('SMS');
  const [dynamicElement, setDynamicElement] = useState(null);

  useEffect(() => {
    templatesCallback()
      .then(({ data }) => {
        setTemplates(
          data?.map((t) => ({
            ...t,
            ...(t?.content ? { content: decodeBase64Auto(t?.content) } : {}),
            ...(t?.body ? { body: JSON.parse(decodeBase64Auto(t?.body)) } : {}),
            ...(t?.structure
              ? { structure: JSON.parse(decodeBase64Auto(t?.structure)) }
              : {}),
          }))
        );
      })
      .catch(() => {
        setTemplates([]);
      });
  }, []);

  const handleClickDynamicElement = (newValue) => {
    setDynamicElement({ value: newValue });
  };

  const onChangeMsgType = (e, newValue) => {
    setSelectedTemplate(null);
    setMsgType(newValue);
  };

  const handleSelectTemplate = (templateId) => {
    const index = templates.findIndex(({ id }) => id === templateId);
    setSelectedTemplate(index);
  };

  const handleCreateTemplate = (templateName, title = '') => {
    let templateObj = {};
    if (editorType === 'LANDING_PAGES') {
      templateObj = {
        isNew: true,
        id: `${templateName}-${templates?.length}`, //Temporary id
        name: templateName,
      };
    } else {
      templateObj = {
        isNew: true,
        id: `${templateName}-${templates?.length}`, //Temporary id
        name: templateName,
        ...(msgType === 'EMAIL' ? { title } : {}),
        content: '',
        scopeId: msgType,
        outcomes: [],
      };
    }

    setSelectedTemplate(templates?.length);
    setTemplates((old) => [...old, templateObj]);
  };

  const handleEditTemplateList = (templateObj, deleteElement = false) => {
    const copyTemplates = [...templates];
    const index = templates.findIndex(({ id }) => id === templateObj?.id);
    if (index !== null) {
      if (deleteElement) copyTemplates.splice(index, 1);
      else copyTemplates.splice(index, 1, templateObj);

      setTemplates(copyTemplates);
    }
  };

  const handleSaveTemplate = (templateObj, type = 'TEMPLATES') => {
    if (type === 'TEMPLATES' && templateObj?.outcomes.length < 1)
      showSnackbar({
        severity: 'warning',
        message: intl.messages.SELECT_AT_LEAST_ONE_OUTCOME,
      });
    else {
      const payload = templateObj;
      if (payload?.isNew) delete payload?.id;
      (payload?.isNew ? createCallback(payload) : editCallback(payload))
        .then(({ data }) => {
          const newEl = payload?.isNew ? data : payload;
          handleEditTemplateList({
            ...newEl,
            ...(newEl?.content
              ? { content: decodeBase64Auto(newEl?.content) }
              : {}),
            ...(type === 'LANDING'
              ? { body: JSON.parse(decodeBase64Auto(newEl?.body)) } //atob converte stringa da Base64 a stringa normale
              : {}),
            ...(msgType === 'EMAIL'
              ? { structure: JSON.parse(decodeBase64Auto(newEl?.structure)) }
              : {}),
          });
          showSnackbar({
            severity: 'success',
            message: payload?.isNew
              ? intl.messages.CREATED_SUCCESSFULLY
              : intl.messages.EDITED_SUCCESSFULLY,
          });
        })
        .catch((err) => {
          showSnackbar({
            severity: 'error',
            message: `${intl.messages[payload?.isNew ? 'TEMPLATE_CREATION_ERROR' : 'TEMPLATE_EDIT_ERROR']} ${getErrorMessage(err) || ''}`,
          });
        });
    }
  };

  const ContentWrapper =
    editorType === 'LANDING_PAGES'
      ? CONTENT_WRAPPERS.LANDING_PAGES
      : CONTENT_WRAPPERS[msgType];

  return (
    <div
      style={{
        display: 'flex',
        height: '100%',
        width: '100%',
        overflow: 'hidden',
      }}
    >
      <LeftBar
        msgType={msgType}
        editorType={editorType}
        onChangeMsgType={onChangeMsgType}
        templates={templates.filter(
          ({ scopeId }) => editorType === 'LANDING_PAGES' || scopeId === msgType
        )}
        handleSelectTemplate={handleSelectTemplate}
        handleCreateTemplate={handleCreateTemplate}
        handleEditTemplateList={handleEditTemplateList}
        handleSaveTemplate={handleSaveTemplate}
      />
      <Box
        sx={{
          flexGrow: 1,
          display: 'flex',
          alignItems: 'center',
          flexDirection: 'column',
          width: '60%',

          padding:
            editorType !== 'LANDING_PAGES' && msgType === 'EMAIL'
              ? '2%'
              : '2% 0 0 0',
        }}
      >
        {selectedTemplate !== null && selectedTemplate !== undefined ? (
          <ContentWrapper
            template={templates[selectedTemplate]}
            handleEditTemplateList={handleEditTemplateList}
            handleSave={handleSaveTemplate}
            dynamicElement={dynamicElement}
          />
        ) : (
          <ContentError
            icon={
              <PanToolAltRoundedIcon
                sx={{ fontSize: '5vw', color: '#acacac' }}
              />
            }
            text={intl.messages.SELECT_A_TEMPLATE}
          />
        )}
      </Box>
      <RightBar
        editorType={editorType}
        handleClickDynamicElement={handleClickDynamicElement}
      />
    </div>
  );
};

EditorWrapper.propTypes = {
  editorType: PropTypes.string,
  templatesCallback: PropTypes.func,
  createCallback: PropTypes.func,
  editCallback: PropTypes.func,
};

export default EditorWrapper;
